// victoria-client/src/components/demand-chart/ChartCanvas.tsx
import { useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import type { ChartData } from "chart.js/auto";

import { DemandChartSkeleton } from "@/components/ChartSkeleton";
import { TimeseriesCanvas } from "@/components/timeseries-chart/TimeseriesCanvas";
import { parseDate } from "@/utils/formatters";

import { rangeSelectPlugin } from "./plugins/rangeSelect";
import { RangeSelectPopup } from "./RangeSelectPopup";
import type { Aggregation, Metric, Point, ViewRange } from "./types";
import { useRangeSelection } from "./useRangeSelection";

const DAY_MS = 24 * 60 * 60 * 1000;
const PERIOD_DAYS_BY_AGG: Record<Aggregation, number> = {
  week: 7,
  month: 30,
  quarter: 90,
};

export type ChartCanvasProps = {
  data: ChartData<"line", Point[]>;
  dataSpan: ViewRange;
  viewRange: ViewRange;
  aggregation: Aggregation;
  metric: Metric;
  todayValue?: string;
  dailyHistory: Point[];
  dailyForecast?: Point[];
  isLoading: boolean;
  onUserRange: (range: ViewRange) => void;
  height?: number;
};

export function ChartCanvas({
  data,
  dataSpan,
  viewRange,
  aggregation,
  metric,
  todayValue,
  dailyHistory,
  dailyForecast = [],
  isLoading,
  onUserRange,
  height = 420,
}: ChartCanvasProps) {
  const { t } = useTranslation();
  const periodDays = PERIOD_DAYS_BY_AGG[aggregation];
  const todayMs = todayValue ? parseDate(todayValue).getTime() : null;

  const rangeSelection = useRangeSelection({
    dailyHistory,
    dailyForecast,
    periodDays,
    todayMs,
  });

  const yAxisLabel = `${
    metric === "revenue"
      ? t("components.demandChart.axis.revenue")
      : t("components.demandChart.axis.demand")
  } / ${aggregation}`;

  // Stable `pluginOptions` identity: TimeseriesCanvas lists this object in its
  // `options` useMemo deps, so a fresh literal each render would rebuild the
  // Chart.js options (and force a <Line> update) on every frame of a drag-select.
  // `onChange`/`onEnd` are already stable useCallbacks.
  const pluginOptions = useMemo(
    () => ({
      rangeSelect: {
        onChange: rangeSelection.onChange,
        onEnd: rangeSelection.onEnd,
      },
    }),
    [rangeSelection.onChange, rangeSelection.onEnd],
  );

  // The totals popup needs the wrapper width; measure it here.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [wrapperWidth, setWrapperWidth] = useState(0);
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      setWrapperWidth(entries[0]?.contentRect.width ?? 0);
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={wrapperRef}>
      <TimeseriesCanvas
        data={data}
        dataSpan={dataSpan}
        viewRange={viewRange}
        yAxisLabel={yAxisLabel}
        minRangeMs={periodDays * 4 * DAY_MS}
        todayValue={todayValue}
        isLoading={isLoading}
        skeleton={<DemandChartSkeleton height={String(height)} />}
        onUserRange={onUserRange}
        height={height}
        // `rangeSelectPlugin` is intentionally NOT registered globally — it's
        // passed only via the per-instance `plugins` prop on TimeseriesCanvas's
        // <Line>. Otherwise its canvas event listeners would attach to every
        // Chart.js chart in the app (e.g. the stock chart on the same item page).
        plugins={[rangeSelectPlugin]}
        pluginOptions={pluginOptions}
      >
        <RangeSelectPopup
          selection={rangeSelection.selection}
          totals={rangeSelection.totals}
          wrapperWidth={wrapperWidth}
          aggregation={aggregation}
          metric={metric}
        />
      </TimeseriesCanvas>
    </div>
  );
}
